import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Download, Eye, Book, Cloud, BarChart3, Shield, Brain, Users } from 'lucide-react';

const ResourceLibrary = () => {
  const [activeFilter, setActiveFilter] = useState('Tous');

  const categories = ['Tous', 'Ebooks', 'Guides Pratiques', 'Études de Cas', 'Webinaires'];

  const resources = [
    {
      id: 1,
      type: 'Guide Pratique',
      category: 'Guides Pratiques',
      title: 'Guide de Transformation Digitale 2024',
      desc: 'Un guide complet pour naviguer dans l’ère numérique, comprenant des feuilles de route.',
      icon: <Book className="w-8 h-8 text-slate-400" />,
      btnLabel: 'Télécharger',
      btnIcon: <Download size={16} />
    },
    {
      id: 2,
      type: 'Ebook',
      category: 'Ebooks',
      title: 'Stratégies Cloud Modernes',
      desc: 'Découvrez comment le cloud hybride et le multi-cloud peuvent propulser votre croissance.',
      icon: <Cloud className="w-8 h-8 text-slate-400" />,
      btnLabel: 'Télécharger',
      btnIcon: <Download size={16} />
    },
    {
      id: 3,
      type: 'Étude de Cas',
      category: 'Études de Cas',
      title: 'Optimisation des Processus',
      desc: 'Comment une entreprise du CAC40 a réduit ses coûts opérationnels de 30% grâce à nous.',
      icon: <BarChart3 className="w-8 h-8 text-slate-400" />,
      btnLabel: 'Lire l’étude',
      btnIcon: <Eye size={16} />
    },
    {
      id: 4,
      type: 'Guide Pratique',
      category: 'Guides Pratiques',
      title: 'Sécurité des Données 2024',
      desc: 'Les meilleures pratiques essentielles pour protéger vos actifs numériques contre les menaces.',
      icon: <Shield className="w-8 h-8 text-slate-400" />,
      btnLabel: 'Télécharger',
      btnIcon: <Download size={16} />
    },
    {
      id: 5,
      type: 'Ebook',
      category: 'Ebooks',
      title: 'L’IA au Service des PME',
      desc: 'Démystifier l’intelligence artificielle et l’intégrer efficacement dans vos workflows.',
      icon: <Brain className="w-8 h-8 text-slate-400" />,
      btnLabel: 'Télécharger',
      btnIcon: <Download size={16} />
    },
    {
      id: 6,
      type: 'Guide Pratique',
      category: 'Guides Pratiques',
      title: 'Collaboration à Distance',
      desc: 'Outils et méthodes pour maintenir une productivité maximale et une cohésion d’équipe.',
      icon: <Users className="w-8 h-8 text-slate-400" />,
      btnLabel: 'Télécharger',
      btnIcon: <Download size={16} />
    }
  ];

  const filteredResources = activeFilter === 'Tous' 
    ? resources 
    : resources.filter(res => res.category === activeFilter);

  return (
    <section id="library-section" className="w-full py-20 px-6 bg-[#F8FAFC]">
      <div className="max-w-[1200px] mx-auto">
        
        {/* Filtres */}
        <div className="flex flex-wrap items-center gap-3 mb-12">
          <span className="text-[11px] font-black text-slate-400 uppercase tracking-widest mr-2">Filtrer par :</span>
          {categories.map((cat) => (
            <button
              key={cat}
              onClick={() => setActiveFilter(cat)}
              className={`px-5 py-2 rounded-full text-sm font-bold transition-all duration-300 ${
                activeFilter === cat 
                ? 'bg-[#1e3a5f] text-white shadow-lg shadow-blue-900/20' 
                : 'bg-white text-slate-500 border border-slate-200 hover:border-[#1e3a5f] hover:text-[#1e3a5f]'
              }`}
            >
              {cat}
            </button>
          ))}
        </div>

        {/* Grille de Cartes */}
        <motion.div 
          layout
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
        >
          <AnimatePresence mode='popLayout'>
            {filteredResources.map((item) => (
              <motion.div
                key={item.id}
                layout
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.9 }}
                transition={{ duration: 0.4 }}
                className="group bg-white rounded-2xl border border-slate-100 p-6 shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-300"
              >
                {/* Image Placeholder */}
                <div className="w-full h-48 bg-slate-50 rounded-xl mb-6 flex items-center justify-center group-hover:bg-blue-50 transition-colors">
                  {item.icon}
                </div>

                {/* Badge Type */}
                <span className="text-[10px] font-black uppercase tracking-widest text-blue-500 mb-3 block">
                  {item.type}
                </span>

                {/* Titre */}
                <h3 className="text-[#0f172a] text-lg font-black mb-3 leading-tight group-hover:text-[#1e3a5f] transition-colors">
                  {item.title}
                </h3>

                {/* Description */}
                <p className="text-slate-500 text-sm font-medium leading-relaxed mb-8">
                  {item.desc}
                </p>

                {/* Bouton Action */}
                <button className="w-full bg-[#1e3a5f] text-white py-3.5 rounded-xl font-bold flex items-center justify-center gap-2 hover:bg-[#0f172a] transition-all duration-300 shadow-md hover:shadow-lg shadow-blue-900/5">
                  {item.btnIcon}
                  {item.btnLabel}
                </button>
              </motion.div>
            ))}
          </AnimatePresence>
        </motion.div>

        {/* Charger Plus */}
        <div className="mt-16 flex justify-center">
          <button className="px-8 py-3 border-2 border-blue-100 text-[#1e3a5f] rounded-xl font-bold text-sm hover:bg-blue-50 hover:border-blue-200 transition-all active:scale-95">
            Charger plus de ressources
          </button>
        </div>
      </div>
    </section>
  );
};

export default ResourceLibrary;